import { Button, focusRing } from '@/design-system';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { FINDING_FILTERS, type FindingFilter } from '../reportIndex';

export interface ReportToolbarProps {
    filter: FindingFilter;
    onFilter: (filter: FindingFilter) => void;
    /** Per filter hoeveel bevindingen erin vallen. */
    counts: Record<FindingFilter, number>;
    total: number;
    /** Met de kosten verborgen bestaat het filter "Met kosten" niet. */
    showCosts: boolean;
    allOpen: boolean;
    onAllOpen: (open: boolean) => void;
    copy: PublicCopy;
}

/** Een stip in de kleur van de ernst, naast het woord: kleur is nooit het enige teken. */
const dots: Partial<Record<FindingFilter, string>> = {
    gebreken: 'bg-[var(--danger)]',
    aandacht: 'bg-[var(--warn)]',
};

/**
 * Boven het volledige rapport: filteren op gebreken, aandachtspunten of kosten,
 * met het aantal erbij, een regel die zegt hoeveel er nu te zien is, en alles
 * in één keer open- of dichtklappen.
 *
 * De filters zijn knoppen die aan of uit staan (`aria-pressed`), in één groep;
 * een filter zonder bevindingen staat erbij, maar is niet te kiezen.
 */
export function ReportToolbar({
    filter,
    onFilter,
    counts,
    total,
    showCosts,
    allOpen,
    onAllOpen,
    copy,
}: ReportToolbarProps) {
    const filters = FINDING_FILTERS.filter((key) => key !== 'kosten' || showCosts);

    return (
        <div className="mb-5 flex flex-col gap-3">
            <div role="group" aria-label={copy.filterLabel} className="flex flex-wrap gap-2">
                {filters.map((key) => {
                    const pressed = key === filter;
                    const empty = key !== 'alles' && counts[key] === 0;
                    const dot = dots[key];

                    return (
                        <button
                            key={key}
                            type="button"
                            aria-pressed={pressed}
                            disabled={empty}
                            onClick={() => onFilter(key)}
                            className={cn(
                                'inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-[22px] border px-4',
                                'font-[family-name:var(--font-body)] text-[13px] leading-[1.25] transition-colors duration-100',
                                pressed
                                    ? 'border-[var(--accent)] bg-[var(--accent-tint)] font-medium text-[var(--accent-dark)]'
                                    : 'border-[var(--border-control)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--text-primary)]',
                                'disabled:cursor-default disabled:opacity-50 disabled:hover:border-[var(--border-control)] disabled:hover:text-[var(--text-secondary)]',
                                focusRing,
                            )}
                        >
                            {dot ? (
                                <span
                                    aria-hidden="true"
                                    className={cn('size-2 shrink-0 rounded-full', dot)}
                                />
                            ) : null}
                            {copy.filters[key]}
                            <span
                                className={cn(
                                    'font-[family-name:var(--font-mono)] text-[12px]',
                                    pressed
                                        ? 'text-[var(--accent-dark)]'
                                        : 'text-[var(--text-muted-mono-safe)]',
                                )}
                            >
                                {counts[key]}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <p
                    role="status"
                    className="m-0 text-[13px] leading-[1.4] text-[var(--text-tertiary-safe)]"
                >
                    {copy.filterStatus(counts[filter], total, filter !== 'alles')}
                </p>
                <Button
                    variant="ghost"
                    size="md"
                    className="-mr-2 min-h-[44px]"
                    onClick={() => onAllOpen(!allOpen)}
                >
                    {allOpen ? copy.collapseAll : copy.expandAll}
                </Button>
            </div>
        </div>
    );
}
